import { useCallback, useEffect, useMemo, useState } from 'react'
import useDeepCompareEffect from 'use-deep-compare-effect'

import { ALL_TIME_RANGE_QUERY } from 'consts/filterOptions'
import type { Providers } from 'generated/graphql'
import { useElectric } from 'hooks/use-electric'
import type { NicheElectricType, NicheStatusElectricType } from 'hooks/use-electric/types'
import { useTerritoryLazyQueries } from 'hooks-queries/territory'
import { PanelStore } from 'stores'
import getUploadDateByValue from 'utils/getUploadDateByValue'
import { PROVIDER_BY_KEY } from 'utils/providersUtils/consts'
import { getFormatedDateVariablesForTerritory, getTerritoryLinkByType } from 'utils/territoryUtils'

import { GET_TERRITORIES_BATCH_LOG } from './use-brand-niche-data-batch.consts'
import type {
  IUseBrandNicheDataProps,
  IUseBrandNicheDataReturn,
  NichesWithData,
} from './use-brand-niche-data-batch.types'

/**
 * Optimized hook for fetching brand niche data using batch queries.
 * This hook consolidates multiple individual territory queries into a single batch request.
 * Use this instead of useBrandNicheData when you need better performance with multiple niches.
 */
export const useBrandNicheDataBatch = ({
  providers,
  uploadDateParam,
  contextHash,
  targetTerritoryId,
  hashError = false,
}: IUseBrandNicheDataProps): IUseBrandNicheDataReturn => {
  const [nichesWithData, setNichesWithData] = useState<NichesWithData>({})
  const [isBatchLoading, setIsBatchLoading] = useState(false)

  const workspaceId = Object(PanelStore?.selectedPanel)?.panelId

  const {
    data: nicheStatus,
    error: errorStatus,
    handleRefetch: refetchStatus,
    unsubscribe: unsubscribeStatus,
  } = useElectric<NicheStatusElectricType>({
    table: 'generated_niche',
    skip: !contextHash,
    variables: `id = '${contextHash}' AND deleted_at IS NULL`,
  })

  useEffect(() => {
    setNichesWithData({})
  }, [contextHash])

  const generatedNicheId = useMemo(() => {
    return nicheStatus?.[0]?.id
  }, [nicheStatus])

  const generatedNicheStatus = useMemo(() => {
    return nicheStatus?.[0]?.status
  }, [nicheStatus])

  const {
    data: nicheData,
    isInitialized,
    error: errorNicheList,
    handleRefetch: refetchNicheList,
    unsubscribe: unsubscribeNicheList,
  } = useElectric<NicheElectricType>({
    table: 'niche',
    variables: `generated_niche_id = '${generatedNicheId}' AND deleted_at IS NULL`,
    skip: !generatedNicheId,
  })

  const handleRefetch = useCallback(() => {
    if (errorStatus || generatedNicheStatus === 'error') {
      refetchStatus()
      return
    }
    if (errorNicheList) {
      refetchNicheList()
    }
  }, [errorNicheList, errorStatus, generatedNicheStatus, refetchNicheList, refetchStatus])

  const mappedProviders = useMemo(
    () => providers.map(p => PROVIDER_BY_KEY[p]).filter((p): p is Providers => p !== undefined),
    [providers]
  )

  const [getTerritoriesBatch] = useTerritoryLazyQueries({
    queryName: 'listTerritoriesWithMetrics',
    errorLog: GET_TERRITORIES_BATCH_LOG,
    queryVariables: {
      ids: [],
    },
  })

  const nichesList = useMemo(() => {
    return nicheData.map(item => ({
      name: item?.name,
      territoryId: item?.id.includes('topic') ? item?.id : `niche.${item?.id}`,
    }))
  }, [nicheData])

  const getQueryVariables = useCallback(() => {
    const dateValue = getFormatedDateVariablesForTerritory(uploadDateParam)
    const isCustomDate = uploadDateParam?.includes('custom')
    const isAllTime = uploadDateParam === 'all'

    const shouldUseTimeRange = !isCustomDate && !isAllTime
    const publishedAtValue = isCustomDate ? getUploadDateByValue(uploadDateParam) : ALL_TIME_RANGE_QUERY

    return {
      providers: mappedProviders,
      ...(shouldUseTimeRange
        ? {
            timeRange: {
              timeUnit: dateValue?.timeUnit,
              timeValue: dateValue?.timeValue,
            },
          }
        : {
            publishedAt: publishedAtValue,
            timeRange: null,
          }),
    }
  }, [mappedProviders, uploadDateParam])

  const unsubscribeAll = useCallback(() => {
    unsubscribeNicheList()
    unsubscribeStatus()
  }, [unsubscribeNicheList, unsubscribeStatus])

  // Batch fetch all territories at once
  const handleBatchFetch = useCallback(
    async (territoryIds: string[]) => {
      if (territoryIds.length === 0) return

      setIsBatchLoading(true)
      const baseQueryVariables = getQueryVariables()

      try {
        const { data, error } = await getTerritoriesBatch({
          variables: {
            ids: territoryIds,
            ...baseQueryVariables,
          },
        })

        if (error) {
          setNichesWithData(prevState => {
            const newState = { ...prevState }
            territoryIds.forEach(id => {
              if (newState[id]) {
                newState[id] = {
                  ...newState[id],
                  isLoading: false,
                  error: error,
                  retryCount: 1,
                }
              }
            })
            return newState
          })
          return
        }

        if (data?.listTerritories) {
          setNichesWithData(prevState => {
            const newState = { ...prevState }
            data.listTerritories.forEach(territory => {
              if (territory?.territoryId && newState[territory.territoryId]) {
                newState[territory.territoryId] = {
                  ...newState[territory.territoryId],
                  isLoading: false,
                  data: territory,
                  error: null,
                }
              }
            })
            // Mark any missing territories as having no data
            territoryIds.forEach(id => {
              const found = data.listTerritories.some(t => t?.territoryId === id)
              if (!found && newState[id]) {
                newState[id] = {
                  ...newState[id],
                  isLoading: false,
                  data: null,
                  error: null,
                }
              }
            })
            return newState
          })
        }
      } finally {
        setIsBatchLoading(false)
      }
    },
    [getQueryVariables, getTerritoriesBatch]
  )

  // Initialize state when niches list changes
  useDeepCompareEffect(() => {
    const arr = nichesList.map(item => ({
      isLoading: true,
      id: item.territoryId,
      data: null,
      name: item.name,
      providers,
      uploadDateParam,
      error: null,
      retryCount: 0,
    }))
    const newState = arr.reduce(
      (acc, item) => ({
        ...acc,
        [item.id]: item,
      }),
      {} as NichesWithData
    )

    setNichesWithData(newState)
  }, [nichesList])

  // Trigger batch fetch when niches list or params change
  useDeepCompareEffect(() => {
    if (nichesList.length === 0 || isBatchLoading) return

    const territoryIds = nichesList.map(n => n.territoryId)
    handleBatchFetch(territoryIds)
  }, [nichesList, providers, uploadDateParam, handleBatchFetch, isBatchLoading])

  const niches = useMemo(() => {
    const nichesKeys = Object.keys(nichesWithData)
    const nichesArray = nichesKeys.map(item => nichesWithData[item])

    const validNiches = nichesArray.filter(item => {
      const hasError = !!item.error
      const hasRetried = item.retryCount > 0
      const isNotLoading = !item.isLoading
      if (hasError && hasRetried && isNotLoading) {
        return false
      }
      const totalPosts = item?.data?.territoryPerformance?.totalPosts
      return totalPosts !== undefined && totalPosts !== 0
    })

    const formattedNiches = validNiches.map(item => {
      const data = item?.data
      const territoryId = item?.id || data?.territoryId
      const nicheIdForUrl = territoryId?.split('.')?.[1]
      const url = getTerritoryLinkByType('niche', nicheIdForUrl, workspaceId)
      return {
        name: data?.name || item?.name,
        totalPosts: data?.territoryPerformance?.totalPosts ?? -1,
        avgEngagement: data?.territoryPerformance?.sampledMetrics?.avgEngagement ?? -1,
        avgViews: data?.territoryPerformance?.sampledMetrics?.avgViews ?? -1,
        territoryId: data?.territoryId || item?.id,
        href: url,
      }
    })
    return formattedNiches
  }, [nichesWithData, workspaceId])

  const allNichesHaveErrors = useMemo(() => {
    const nichesKeys = Object.keys(nichesWithData)
    const nichesArray = nichesKeys.map(item => nichesWithData[item])
    return nichesArray.every(item => item.error) && nichesArray.length > 0
  }, [nichesWithData])

  const isNichesDataLoading = useMemo(() => {
    const nichesKeys = Object.keys(nichesWithData)
    if (nichesKeys.length === 0) return false
    const nichesArray = nichesKeys.map(item => nichesWithData[item])
    return nichesArray.some(item => item.isLoading)
  }, [nichesWithData])

  const hasError = useMemo(() => {
    return Boolean(errorStatus || errorNicheList || allNichesHaveErrors)
  }, [errorNicheList, errorStatus, allNichesHaveErrors])

  const isLoading = useMemo(() => {
    return (
      !targetTerritoryId ||
      (!contextHash && !hashError) ||
      (!hashError && (generatedNicheStatus === 'pending' || generatedNicheStatus === undefined)) ||
      (!isInitialized && generatedNicheStatus === 'completed')
    )
  }, [targetTerritoryId, contextHash, hashError, generatedNicheStatus, isInitialized])

  return {
    workspaceId,
    niches,
    nicheStatus: generatedNicheStatus,
    isNicheListInitialized: isInitialized,
    isNichesDataLoading,
    hashError,
    hasError,
    isLoading,
    refetch: handleRefetch,
    unsubscribe: unsubscribeAll,
  }
}
